import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Mail, Linkedin, Send } from "lucide-react";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success("Message sent successfully. We'll get back to you soon.");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch {
      toast.error("Something went wrong. Please try again later.");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>Contact Us | FutureThought</title>
        <meta name="description" content="Get in touch with FutureThought. Reach out for inquiries about technology content, freelance services, partnerships, or general questions." />
        <link rel="canonical" href="https://futurethought.emergent.host/contact" />
      </Helmet>

      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold font-['Playfair_Display']">
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Mail size={20} className="text-[hsl(var(--primary))]" />
            <span className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))]">Get in Touch</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-['Playfair_Display'] font-bold tracking-tight mb-4" data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] mb-12 max-w-2xl">
            Have a question, partnership inquiry, or want to discuss freelance content services? We would love to hear from you.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[hsl(var(--foreground))]">
                      Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[hsl(var(--primary))] focus:outline-none transition-colors"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[hsl(var(--foreground))]">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[hsl(var(--primary))] focus:outline-none transition-colors"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[hsl(var(--foreground))]">Subject</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[hsl(var(--primary))] focus:outline-none transition-colors"
                    placeholder="What is this about?"
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[hsl(var(--foreground))]">
                    Message <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[hsl(var(--primary))] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us what you're looking for..."
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="px-8 py-3 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  <Send size={16} />
                  {sending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              <div className="border border-[hsl(var(--border))] p-6 bg-[hsl(var(--card))]">
                <h3 className="text-lg font-['Playfair_Display'] font-semibold mb-4">Reach Us Directly</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail size={18} className="text-[hsl(var(--primary))] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Email</p>
                      <a href="mailto:bhuminderjit@gmail.com" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors">
                        bhuminderjit@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Linkedin size={18} className="text-[hsl(var(--primary))] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">LinkedIn</p>
                      <a href="https://www.linkedin.com/company/futurethought/" target="_blank" rel="noopener noreferrer" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors">
                        FutureThought
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-[hsl(var(--border))] p-6 bg-[hsl(var(--card))]">
                <h3 className="text-lg font-['Playfair_Display'] font-semibold mb-4">What We Can Help With</h3>
                <ul className="space-y-3 text-sm text-[hsl(var(--muted-foreground))]">
                  <li className="flex items-start gap-2">
                    <span className="text-[hsl(var(--primary))] mt-1">-</span>
                    Technology content and thought leadership writing
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[hsl(var(--primary))] mt-1">-</span>
                    B2B content strategy and consulting
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[hsl(var(--primary))] mt-1">-</span>
                    Partnership and collaboration inquiries
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[hsl(var(--primary))] mt-1">-</span>
                    Advertising and sponsorship opportunities
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[hsl(var(--primary))] mt-1">-</span>
                    General questions or feedback
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="text-xl font-bold font-['Playfair_Display']">
            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </Link>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <Link to="/privacy-policy" className="hover:text-[hsl(var(--foreground))] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[hsl(var(--foreground))] transition-colors">Terms of Service</Link>
            <span className="text-[hsl(var(--foreground))]">Contact</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
